import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, map, Subject, switchMap, take, takeUntil } from "rxjs";
import { MatTemplateType } from "../select-form-control/select-form-control.service";
import { _IDataSourceControlBase } from "../types/i-data-source-control.base";
import { SubjectMapped } from "../types/subject.mapped";
import { ControlDataSource } from "./control-data-source";

@Injectable()
export class DataSourceControlService implements Required<SubjectMapped<_IDataSourceControlBase>> {

  protected readonly _destroy = new Subject<void>();

  readonly focusedSubject = new BehaviorSubject<boolean>(false)
  readonly useRemotesSubject = new BehaviorSubject<boolean>(false);
  readonly valueExprSubject = new BehaviorSubject<string>('value');
  readonly displayExprSubject = new BehaviorSubject<string | ((data: any) => string)>('display');
  readonly dataSourceSubject = new BehaviorSubject<ControlDataSource<any>>(undefined);
  readonly stopFetchingSubject = new BehaviorSubject<boolean>(false);

  readonly pageSizeSubject = new BehaviorSubject<number>(10)
  readonly currentPageSubject = new BehaviorSubject<number>(0)
  readonly fetchedBatchesSubject = new BehaviorSubject<{ [pageIdx: number]: any[] }>({});
  readonly fetchedDataSubject = new BehaviorSubject<any[]>([]);

  readonly searchValueSubject = new BehaviorSubject<string>(undefined)
  readonly selectedItemSubject = new BehaviorSubject<any>(null)
  readonly valueIsDisplaySubject = new BehaviorSubject<boolean>(false);
  get valueIsDisplay() {
    return this.valueIsDisplaySubject.value;
  }

  readonly locallyFilteredDataSubject = new BehaviorSubject<any[]>([])
  readonly fetchingSubject = new BehaviorSubject<boolean>(false);
  readonly templateSubject = new BehaviorSubject<_IDataSourceControlBase['template']>(undefined);
  readonly dataSubject = new BehaviorSubject<any[]>(undefined);

  readonly valueFromSelectedItemObs = combineLatest([
    this.valueIsDisplaySubject,
    this.selectedItemSubject,
    this.valueExprSubject,
  ])
    .pipe(
      filter(([valueIsDisplay, selectedItem]) => !valueIsDisplay || selectedItem),
      map(([_, selectedItem, valueExpr]) => {

        if (!selectedItem) {
          return null
        };

        if (typeof selectedItem !== 'object') {
          return selectedItem
        }

        if (!valueExpr) {
          return selectedItem.value
        }

        return selectedItem[valueExpr];
      })
    )

  readonly valueSubject = new BehaviorSubject<string | number>(undefined);


  readonly displayObs = combineLatest([
    this.selectedItemSubject,
    this.displayExprSubject,
    this.valueIsDisplaySubject,
    this.valueSubject
  ])
    .pipe(
      takeUntil(this._destroy),
      map(([selectedItem, displayExpr, valueIsDisplay, value]) => {

        if (valueIsDisplay) {
          return value;
        }

        if (!selectedItem) {
          return null;
        }

        if (typeof selectedItem !== 'object') {
          return selectedItem
        }

        if (!displayExpr) {
          return selectedItem.text;
        }

        if (typeof displayExpr === 'string') {
          return selectedItem[displayExpr]
        }

        return displayExpr(selectedItem);
      })
    )

  readonly matTemplateTypeObs = this.templateSubject
    .pipe(
      map((matOptionTemplate): MatTemplateType => Boolean(matOptionTemplate) ? 'fromTemplate' : 'basic')
    )

  constructor() {

    this.fetchedBatchesSubject
      .pipe(takeUntil(this._destroy))
      .subscribe((batches) => {

        const flattenedBatches = Object.values(batches).flatMap(batch => batch)

        this.fetchedDataSubject.next(flattenedBatches);

      })


    const resetObs = combineLatest([
      this.dataSourceSubject.pipe(distinctUntilChanged()),
      this.pageSizeSubject.pipe(distinctUntilChanged()),
      this.useRemotesSubject.pipe(distinctUntilChanged()),
      this.searchValueSubject.pipe(
        map((searchValue) => this.useRemotesSubject.value ? searchValue : null),
        distinctUntilChanged()
      ),
      this.focusedSubject.pipe(filter(Boolean), distinctUntilChanged())
    ]);

    resetObs
      .pipe(
        debounceTime(100),
        takeUntil(this._destroy),
        filter(([dataSource]) => Boolean(dataSource))
      )
      .subscribe((reset) => {
        this.fetchedDataSubject.next([])
        this.fetchedBatchesSubject.next({});
        this.currentPageSubject.next(0);
        this.stopFetchingSubject.next(false);
      })




    this.fetchedDataSubject
      .pipe(takeUntil(this._destroy))
      .subscribe((fetchedData) => {
        this.locallyFilteredDataSubject.next(fetchedData)
      })


    this.valueFromSelectedItemObs
      .pipe(takeUntil(this._destroy))
      .subscribe(value => {
        this.valueSubject.next(value)
      })

    combineLatest([
      this.useRemotesSubject,
      this.searchValueSubject,
      this.fetchedDataSubject,
      this.displayExprSubject
    ])
      .pipe(
        filter(([useRemotes]) => !useRemotes),
        takeUntil(this._destroy),
        map(([_, searchValue, fetchedData, displayExpr]) => ({ searchValue, fetchedData, displayExpr }))
      )
      .subscribe(({ fetchedData, searchValue, displayExpr }) => {

        if (!searchValue) {
          this.locallyFilteredDataSubject.next(fetchedData);
          return;
        }

        const filteredData = fetchedData.filter((row) => {

          if (!displayExpr) {
            return true;
          }

          const display: string = typeof displayExpr === 'function' ? displayExpr(row) : row[displayExpr];

          return display.includes(searchValue);

        });

        this.locallyFilteredDataSubject.next(filteredData);
      })



    this.displayObs
      .pipe(
        takeUntil(this._destroy),
        filter(Boolean)
      )
      .subscribe((display) => {
        if (display === this.searchValueSubject.value) {
          return;
        }

        this.searchValueSubject.next(display)
      })



    this.focusedSubject.pipe(
      filter(Boolean),
      switchMap(() => combineLatest([
        this.dataSourceSubject,
        this.stopFetchingSubject,
        this.useRemotesSubject,
        this.currentPageSubject,
        this.pageSizeSubject,
        this.searchValueSubject,
      ])),
      filter(([dataSource, stopFetching]) => !!dataSource && !stopFetching),
      debounceTime(200),
      takeUntil(this._destroy)
    )
      .subscribe(([dataSource, _, useRemotes, currentPage, pageSize, searchValue]) => {
        this.fetchData({ dataSource, useRemotes, currentPage, pageSize, searchValue })
      })

  }



  fetchData({ currentPage, dataSource, pageSize, searchValue, useRemotes }: { dataSource: ControlDataSource, useRemotes: boolean, currentPage: number, pageSize: number, searchValue: string }) {

    if (!dataSource) {
      return;
    }

    this.fetchingSubject.next(true);

    if (!useRemotes) {

      dataSource.load()
        .pipe(take(1), takeUntil(this._destroy))
        .subscribe((data) => {
          this.fetchedDataSubject.next(data);
          this.fetchingSubject.next(false);
        })

      return;
    }

    dataSource.load({
      searchValue,
      skip: pageSize * currentPage,
      take: pageSize
    })
      .pipe(take(1), takeUntil(this._destroy))
      .subscribe((data) => {

        if (data.length < pageSize) {
          this.stopFetchingSubject.next(true)
        }

        const prevFetchedBatches = this.fetchedBatchesSubject.value;

        this.fetchedBatchesSubject.next({
          ...prevFetchedBatches,
          [currentPage]: data
        })

        this.fetchingSubject.next(false);
      })

  }

  confirmValue(valueKey: string | number) {


    if (!valueKey) {
      this.selectedItemSubject.next(null);
      return;
    }

    const dataSource = this.dataSourceSubject.value;

    if (this.valueIsDisplay) {
      this.valueSubject.next(valueKey);
    }

    if (!dataSource?.byKey) {
      return;
    }


    const fetchedData = this.fetchedDataSubject.value;
    const valueExpr = this.valueExprSubject.value || 'value';

    const selectedItem = fetchedData.find((row) => row[valueExpr] === valueKey);

    if (selectedItem) {
      this.selectedItemSubject.next(selectedItem)
      return;
    }

    dataSource.byKey(valueKey as string)
      .pipe(take(1), takeUntil(this._destroy))
      .subscribe(confirmedValue => {
        this.selectedItemSubject.next(confirmedValue || null)
      })

  }

  dispose() {

    Object.values(this)
      .filter((prop) => prop instanceof BehaviorSubject)
      .forEach((subjectProp: BehaviorSubject<any>) => subjectProp.complete())


    this._destroy.next();
    this._destroy.complete();
  }

}
